import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { BsCheckCircle, BsEnvelope, BsHourglassSplit } from "react-icons/bs";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import StepPanel from "../StepPanel";
import StatusBanner from "../StatusBanner";

const STATUS_LABELS = {
	validating: "Validating",
	ready: "Preparing attach",
	processing: "Attaching files",
	uploading: "Uploading",
};

const SubmittedStep = ({ importData }) => {
	const history = useHistory();
	const [copied, setCopied] = useState(false);
	const status = importData?.status;
	const statusLabel = STATUS_LABELS[status] || status || "Submitted";
	const isValidating = status === "validating";
	const isProcessing = status === "processing" || status === "ready";
	const isInProgress = isValidating || isProcessing;

	const copyId = async () => {
		if (!importData?.id) return;
		try {
			await navigator.clipboard.writeText(importData.id);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			setCopied(false);
		}
	};

	const title = isValidating
		? "Validation in progress"
		: isProcessing
		? "Attaching files"
		: "Batch submitted";

	const description = isValidating
		? "Please stay on this page or wait a moment — we’re checking your book list against the uploaded covers and PDFs."
		: isProcessing
		? "Validation finished. Missing covers and PDFs are being attached now. You can wait here or leave and check email later."
		: "You can leave this page. Validation and attachment continue on the server — no need to wait here.";

	return (
		<StepPanel
			stepNumber={4}
			title={title}
			description={description}
			nav={{
				hideBack: true,
				onNext: () => history.push("/bulk-book-imports"),
				nextLabel: isInProgress ? "Leave and check later" : "Start another import",
			}}
		>
			<div className="mx-auto max-w-lg space-y-6 text-center">
				{isValidating ? (
					<div
						className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-700"
						aria-hidden
					>
						<AiOutlineLoading3Quarters className="h-9 w-9 animate-spin" />
					</div>
				) : isProcessing ? (
					<div
						className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-akgreen bg-opacity-10 text-akgreen"
						aria-hidden
					>
						<AiOutlineLoading3Quarters className="h-9 w-9 animate-spin" />
					</div>
				) : (
					<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-akgreen bg-opacity-10 text-akgreen animate-success-bounce">
						<BsCheckCircle className="h-9 w-9" aria-hidden />
					</div>
				)}

				<div className="space-y-2">
					<p className="text-lg font-semibold text-akgreenest">
						{isValidating
							? "Please wait while we validate your batch"
							: isProcessing
							? "Almost done — attaching media"
							: "Your files were accepted"}
					</p>
					<p className="text-sm leading-relaxed text-gray-600">
						{isValidating
							? "This usually takes a short while depending on how many rows and files you submitted. This screen updates automatically when validation finishes."
							: isProcessing
							? "We’ll attach missing covers and PDFs (never replacing existing media) and email you an Excel report when finished."
							: "We’ll validate every row, attach missing covers and PDFs (never replacing existing media), and email you an Excel report when finished."}
					</p>
				</div>

				<div
					className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${
						isValidating
							? "border-amber-200 bg-amber-50 text-amber-900"
							: "border-akgreen border-opacity-20 bg-akgreen bg-opacity-5 text-akgreenest"
					}`}
					role="status"
					aria-live="polite"
				>
					{isInProgress ? (
						<AiOutlineLoading3Quarters
							className="h-3.5 w-3.5 animate-spin"
							aria-hidden
						/>
					) : (
						<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-akgreen" />
					)}
					{statusLabel}
				</div>

				{isValidating ? (
					<StatusBanner
						tone="warning"
						title="Validation underway — please wait"
						className="text-left"
					>
						<div className="flex items-start gap-2">
							<BsHourglassSplit
								className="mt-0.5 h-4 w-4 flex-shrink-0"
								aria-hidden
							/>
							<span>
								Stay here until validation finishes, or leave and reopen
								this import from the start screen. We’ll email you if
								anything needs fixing.
							</span>
						</div>
					</StatusBanner>
				) : (
					<StatusBanner
						tone="info"
						title="Watch your inbox — or download here when finished"
						className="text-left"
					>
						<div className="flex items-start gap-2">
							<BsEnvelope
								className="mt-0.5 h-4 w-4 flex-shrink-0"
								aria-hidden
							/>
							<span>
								When finished, download the Excel report here or from
								your email.
							</span>
						</div>
					</StatusBanner>
				)}

				{importData?.id ? (
					<button
						type="button"
						onClick={copyId}
						className="text-xs text-gray-500 underline-offset-2 hover:text-akgreenest hover:underline"
					>
						{copied ? "Import ID copied" : "Copy import ID"}
					</button>
				) : null}
			</div>
		</StepPanel>
	);
};

export default SubmittedStep;
